import parse, {
  domToReact,
  Element,
  HTMLReactParserOptions,
  DOMNode,
} from 'html-react-parser';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Blockquote } from '@/app/_components/core/blockquote';
import { Button, ButtonVariant } from '@/app/_components/core/button';
import { Copytext, type CopytextVariant } from '@/app/_components/core/copytext';
import { DownloadButton, DownloadLink } from '@/app/_components/core/download-button';
import { Headline } from '@/app/_components/core/headline';
import { List, type ListVariant } from '@/app/_components/core/list';
import { SmartLink } from '@/app/_components/core/smart-link';
import { TextLink } from '@/app/_components/core/text-link';
import { cn } from '@/app/_lib/utils';
import { getDownloadUrl, getFilenameFromUrl } from './download-url';

// Map generic icon names to React components (CMS-agnostic, type-safe)
type IconName = 'arrow-right' | 'chevron-right';
const ICON_MAP: Record<IconName, React.ReactNode> = {
  'arrow-right': <ArrowRight />,
  'chevron-right': <ChevronRight />,
};

// Get icon component from generic icon name (type-safe lookup)
const getIconFromName = (iconName: string | undefined): React.ReactNode | null => {
  if (!iconName) return null;
  if (iconName in ICON_MAP) {
    return ICON_MAP[iconName as IconName];
  }
  return null;
};

// Check if element contains only whitespace or &nbsp;
const isEmptyElement = (element: Element): boolean => {
  if (element.children.length === 0) return true;

  // Check if all children are just whitespace or &nbsp;
  return element.children.every((child) => {
    // domhandler's ElementType is a string enum whose `Text` value is exactly
    // 'text', so the comparison is safe (instanceof is unreliable across module
    // copies). `\s` already matches the non-breaking space (&nbsp;).
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- string-enum value equals this literal at runtime
    if (child.type === 'text') {
      const text = child.data;
      return !text || /^\s*$/.test(text);
    }
    return false;
  });
};

export interface RichTextClassConfig {
  h1?: string;
  h2?: string;
  h3?: string;
  h4?: string;
  copytext?: string;
  list?: string;
  blockquote?: string;
  link?: string;
}

/**
 * Custom replace function for parseRichText.
 * Called BEFORE standard replacements (h1→Headline, p→Copytext, etc.).
 *
 * @param element - The DOM element being processed
 * @returns
 *   - JSX element: Use this instead of standard handling
 *   - null/undefined: Continue with standard handling
 */
export type CustomReplaceFn = (element: Element) => React.JSX.Element | null | undefined;

export interface ParseRichTextOptions {
  /** Override CSS classes on rendered elements */
  classConfig?: RichTextClassConfig;
  /**
   * Custom replace function called before standard replacements.
   * Useful for handling special elements (e.g., HubSpot forms, custom embeds).
   */
  customReplace?: CustomReplaceFn;
}

// Replace heading tags with Headline component. h5/h6 use h4 styling since we
// don't have smaller headline styles.
function renderHeading(
  domNode: Element,
  options: HTMLReactParserOptions,
  opts?: ParseRichTextOptions
): React.JSX.Element {
  const children = domToReact(domNode.children as DOMNode[], options);
  const tag = domNode.name as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  const styledAs = tag === 'h5' || tag === 'h6' ? 'h4' : tag;
  return (
    <Headline as={styledAs} className={opts?.classConfig?.[styledAs]}>
      {children}
    </Headline>
  );
}

// Replace <blockquote> with Blockquote component. Inner <p> uses the 'book'
// Copytext variant (font-normal instead of font-medium).
function renderBlockquote(
  domNode: Element,
  opts?: ParseRichTextOptions
): React.JSX.Element {
  const blockquoteOptions: HTMLReactParserOptions = {
    replace: (node) => {
      if (node instanceof Element && node.name === 'p') {
        const innerChildren = domToReact(node.children as DOMNode[], blockquoteOptions);
        return <Copytext variant="book">{innerChildren}</Copytext>;
      }
    },
  };
  const children = domToReact(domNode.children as DOMNode[], blockquoteOptions);
  return <Blockquote className={opts?.classConfig?.blockquote}>{children}</Blockquote>;
}

// Replace <ul>/<ol> with List component. `data-list` (set by the Typo3 rich
// text transformer) selects the variant.
function renderList(
  domNode: Element,
  options: HTMLReactParserOptions,
  opts?: ParseRichTextOptions
): React.JSX.Element {
  const children = domToReact(domNode.children as DOMNode[], options);
  const listVariant = domNode.attribs['data-list'] as ListVariant | undefined;
  return (
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty/absent variant → 'default'
    <List ordered={domNode.name === 'ol'} variant={listVariant || 'default'} className={opts?.classConfig?.list}>
      {children}
    </List>
  );
}

// Replace <p> with Copytext component (skipping empty paragraphs). `data-copytext`
// (set by the Typo3 rich text transformer) selects the variant.
function renderParagraph(
  domNode: Element,
  options: HTMLReactParserOptions,
  opts?: ParseRichTextOptions
): React.JSX.Element {
  // Skip empty paragraphs (only whitespace or &nbsp;)
  if (isEmptyElement(domNode)) {
    return <></>;
  }
  const children = domToReact(domNode.children as DOMNode[], options);
  const copytextVariant = domNode.attribs['data-copytext'] as CopytextVariant | undefined;
  return (
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty/absent variant → 'default'
    <Copytext variant={copytextVariant || 'default'} className={opts?.classConfig?.copytext}>
      {children}
    </Copytext>
  );
}

interface ButtonLinkProps {
  variant: ButtonVariant;
  icon: React.ReactNode | null;
  childContent: React.ReactNode;
  href: string;
  hasDownload: boolean;
  downloadFilename: string | undefined;
  dataTrackId: string | undefined;
  title: string | undefined;
  target: string | undefined;
  rel: string | undefined;
  linkClassName: string | undefined;
}

// Renders a CMS button-styled link (data-variant present) in one of three
// flavours: download with loading UI, download with toast only, or a plain link.
function renderButtonLink(props: ButtonLinkProps): React.JSX.Element {
  const {
    variant, icon, childContent, href, hasDownload,
    downloadFilename, dataTrackId, title, target, rel, linkClassName,
  } = props;

  const isLinkVariant = variant === 'link';
  const isGhostVariant = variant === 'ghost';
  // ghost and link variants show only a toast, no loading UI
  const useSimpleDownload = hasDownload && (isLinkVariant || isGhostVariant);

  // Cap CTA-style RTE buttons at 288px on mobile (same rule as CtaList). Long
  // link titles truncate with an ellipsis on narrow viewports; from md (≥ 768px)
  // the cap is lifted so full text shows. For link variant, also inherit font
  // size from surrounding text.
  const buttonClassName = isLinkVariant
    ? cn('p-0! h-auto! text-[length:inherit]! max-w-72 md:max-w-none', linkClassName)
    : 'max-w-72 md:max-w-none';

  // Wrap label in a truncating span so long titles get an ellipsis once the
  // parent's max-w-72 kicks in.
  const truncatedLabel = <span className="truncate">{childContent}</span>;

  // Download with loading UI (default, secondary, outline, destructive variants)
  if (hasDownload && !useSimpleDownload) {
    return (
      <DownloadButton
        href={href}
        filename={downloadFilename ?? 'download'}
        variant={variant}
        size={isLinkVariant ? undefined : 'default'}
        className={buttonClassName}
        endIcon={icon}
        data-track-id={dataTrackId}
      >
        {truncatedLabel}
      </DownloadButton>
    );
  }

  // Download without loading UI - only toast (ghost, link variants)
  if (useSimpleDownload) {
    return (
      <Button
        variant={variant}
        size={isLinkVariant ? undefined : 'default'}
        className={buttonClassName}
        asChild
      >
        <DownloadLink
          href={href}
          filename={downloadFilename ?? 'download'}
          endIcon={icon}
          data-track-id={dataTrackId}
        >
          {truncatedLabel}
        </DownloadLink>
      </Button>
    );
  }

  // All link types (external, internal, mailto, tel)
  return (
    <Button
      variant={variant}
      size={isLinkVariant ? undefined : 'default'}
      className={buttonClassName}
      asChild
    >
      <SmartLink href={href} title={title} target={target} rel={rel} data-track-id={dataTrackId}>
        {truncatedLabel}
        {icon && (
          <span className="inline-flex shrink-0 items-center">
            {icon}
          </span>
        )}
      </SmartLink>
    </Button>
  );
}

// Replace <a> with a button-styled link (data-variant present) or a plain
// text link, resolving download links to the proxy URL.
function renderAnchor(
  domNode: Element,
  options: HTMLReactParserOptions,
  opts?: ParseRichTextOptions
): React.JSX.Element {
  const { attribs, children } = domNode;
  const originalHref = attribs.href || '#';
  // If a download attribute exists (even empty), extract the filename from URL
  // and route through the proxy so the Content-Disposition header is set.
  const hasDownload = 'download' in attribs;
  const downloadFilename = hasDownload ? getFilenameFromUrl(originalHref) : undefined;
  const href = hasDownload ? getDownloadUrl(originalHref, downloadFilename) : originalHref;
  const dataVariant = attribs['data-variant'] as ButtonVariant | undefined;
  // Passive tracking key set by the CMS on RTE links — pass it straight through.
  const dataTrackId = attribs['data-track-id'];
  const childContent = domToReact(children as DOMNode[], options);

  if (dataVariant) {
    return renderButtonLink({
      variant: dataVariant,
      icon: getIconFromName(attribs['data-icon']),
      childContent,
      href,
      hasDownload,
      downloadFilename,
      dataTrackId,
      title: attribs.title,
      target: attribs.target,
      rel: attribs.rel,
      linkClassName: opts?.classConfig?.link,
    });
  }

  // Regular text link without button styling. Download text links use
  // DownloadLink (only toast, no loading UI).
  if (hasDownload) {
    return (
      <DownloadLink
        href={href}
        filename={downloadFilename ?? 'download'}
        className="underline underline-offset-2"
        data-track-id={dataTrackId}
      >
        {childContent}
      </DownloadLink>
    );
  }

  return (
    <TextLink href={href} title={attribs.title} target={attribs.target} className={opts?.classConfig?.link} data-track-id={dataTrackId}>
      {childContent}
    </TextLink>
  );
}

export const parseRichText = (html: string, opts?: ParseRichTextOptions) => {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (!(domNode instanceof Element)) return undefined;

      // Custom replace - called first, before standard handling
      if (opts?.customReplace) {
        const customResult = opts.customReplace(domNode);
        if (customResult !== undefined && customResult !== null) {
          return customResult;
        }
      }

      if (/^h[1-6]$/.test(domNode.name)) return renderHeading(domNode, options, opts);
      if (domNode.name === 'blockquote') return renderBlockquote(domNode, opts);
      if (domNode.name === 'ul' || domNode.name === 'ol') return renderList(domNode, options, opts);
      if (domNode.name === 'strong' || domNode.name === 'b') {
        return <strong className="font-bold">{domToReact(domNode.children as DOMNode[], options)}</strong>;
      }
      if (domNode.name === 'p') return renderParagraph(domNode, options, opts);
      if (domNode.name === 'a') return renderAnchor(domNode, options, opts);

      return undefined;
    },
  };

  return parse(html, options);
};
